import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import { CreateLeadFinancialApiPayload } from '@/actions/leads/createLeadFinancial';
import { GetLeadFinancialApiResponse } from '@/actions/leads/getLeadFinancial';
import { updateLeadFinancial } from '@/actions/leads/updateLeadFinancial';
import { ApiResponse } from '@/types/response.type';

export interface UseUpdateLeadFinancialReturn {
  mutate: (
    candidateId: string,
    payload: Omit<CreateLeadFinancialApiPayload, 'zee_id'>
  ) => Promise<ApiResponse<GetLeadFinancialApiResponse> | undefined>;
  result: ApiResponse<GetLeadFinancialApiResponse> | undefined;
  error: Error | null;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
}

export const useUpdateLeadFinancial = (): UseUpdateLeadFinancialReturn => {
  const queryClient = useQueryClient();

  const mutation: UseMutationResult<
    ApiResponse<GetLeadFinancialApiResponse> | undefined,
    Error,
    {
      candidateId: string;
      payload: Omit<CreateLeadFinancialApiPayload, 'zee_id'>;
    }
  > = useMutation({
    mutationFn: ({ payload }) => updateLeadFinancial(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['zee-candidate-financial', variables.candidateId],
      });
    },
  });

  return {
    mutate: (candidateId, payload) =>
      mutation.mutateAsync({ candidateId, payload }),
    result: mutation.data,
    error: mutation.error,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    isLoading: mutation.isPending,
  };
};
